'use client'

import React, { useState, useEffect } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Skeleton } from '@/components/ui/skeleton'
import { GET_CATEGORIES_FOR_SELECT } from '../../Categories/categoriesQueries'
import { UPDATE_PRODUCT_CATEGORIES } from '../productMutations'
import { GET_PRODUCT_DETAILS } from '../productQueries'

interface CategoryEditModalProps {
  isOpen: boolean
  onClose: () => void
  productId: number
  currentCategoryIds: number[]
  language: string
}

export function CategoryEditModal({
  isOpen,
  onClose,
  productId,
  currentCategoryIds,
  language,
}: CategoryEditModalProps) {
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([])

  const { data: categoriesData, loading: categoriesLoading } = useQuery(
    GET_CATEGORIES_FOR_SELECT,
    {
      skip: !isOpen,
    }
  )

  const [updateCategories, { loading: updating }] = useMutation(
    UPDATE_PRODUCT_CATEGORIES,
    {
      refetchQueries: [
        { query: GET_PRODUCT_DETAILS, variables: { id: productId } },
      ],
      onCompleted: () => {
        onClose()
      },
    }
  )

  useEffect(() => {
    if (isOpen) {
      setSelectedCategoryIds(currentCategoryIds)
    }
  }, [isOpen, currentCategoryIds])

  const handleToggleCategory = (categoryId: number) => {
    setSelectedCategoryIds(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    )
  }

  const handleSave = async () => {
    try {
      await updateCategories({
        variables: {
          id: productId,
          categories: selectedCategoryIds.map(id => ({ id })),
        },
      })
    } catch (error) {
      console.error('Error updating categories:', error)
    }
  }

  const handleClose = () => {
    setSelectedCategoryIds(currentCategoryIds)
    onClose()
  }

  const getCategoryName = (category: any) => {
    return (
      category.category_profiles.find(
        (profile: any) => profile.language.toLowerCase() === language
      )?.name || category.category_profiles[0]?.name
    )
  }

  const categories = categoriesData?.findManyCategory || []

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Categories</DialogTitle>
          <DialogDescription>
            Select which categories this product belongs to.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {categoriesLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center space-x-3">
                  <Skeleton className="h-4 w-4" />
                  <Skeleton className="h-4 w-32" />
                </div>
              ))}
            </div>
          ) : categories.length === 0 ? (
            <p className="text-sm text-gray-500">No categories available.</p>
          ) : (
            <div className="grid grid-cols-1 gap-2 max-h-80 overflow-y-auto">
              {categories.map((category: any) => (
                <div
                  key={category.id}
                  className="flex items-center space-x-3 p-2 rounded hover:bg-gray-50"
                >
                  <Checkbox
                    id={`category-${category.id}`}
                    checked={selectedCategoryIds.includes(category.id)}
                    onCheckedChange={() => handleToggleCategory(category.id)}
                  />
                  <label
                    htmlFor={`category-${category.id}`}
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    {getCategoryName(category)}
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={updating || categoriesLoading}>
            {updating ? (
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                Saving...
              </div>
            ) : (
              'Save'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
